import { onBeforeUnmount, onScopeDispose, ref, watch } from 'vue';
import { FilterStoreName } from '../services/api/filter/types';
import { useFilterStoreByName } from '../stores/filter';

interface UseFilterWatchOptions {
    /** Called inside the debounced timeout. Defaults to `filterStore.getFilter()`. */
    onDebounce?: (filterStore: ReturnType<typeof useFilterStoreByName>) => void;
    /** Whether to call getFilter() immediately. Defaults to true. */
    fetchOnInit?: boolean;
    /** Use onScopeDispose instead of onBeforeUnmount for cleanup. */
    scopeDispose?: boolean;
    /** Endpoint to use for getFilter calls. Defaults to 'filter'. Use 'protocols/filter' from protocol views. */
    endpoint?: 'filter' | 'protocols/filter';
}

export function useFilterWatch(name: FilterStoreName, options: UseFilterWatchOptions = {}) {
    const { fetchOnInit = true, scopeDispose = false, endpoint } = options;

    const filterStore = useFilterStoreByName(name);
    const filterLoaded = ref(false);
    const updateFilterTimeout = ref<number | undefined>();
    const filterIsFetched = ref(false);

    const getFilter = () => {
        if (filterIsFetched.value) {
            return;
        }

        filterIsFetched.value = true;

        filterStore.getFilter({ endpoint }).then(() => {
            filterLoaded.value = true;
        });
    };

    if (fetchOnInit) {
        getFilter();
    }

    watch(
        () => filterStore.requestData,
        (newValue, oldValue) => {
            if (!oldValue || JSON.stringify(newValue) === JSON.stringify(oldValue)) {
                return;
            }

            clearTimeout(updateFilterTimeout.value);
            filterStore.abortPendingRequests();

            if (!filterLoaded.value) {
                filterStore.loading = false;
                return;
            }

            filterStore.loading = true;

            updateFilterTimeout.value = window.setTimeout(() => {
                if (options.onDebounce) {
                    options.onDebounce(filterStore);
                } else {
                    filterLoaded.value = false;
                    filterStore.getFilter({ endpoint }).then(() => {
                        filterLoaded.value = true;
                    });
                }
            }, 1000);
        }
    );

    const cleanup = () => {
        clearTimeout(updateFilterTimeout.value);
        filterStore.abortPendingRequests();
        filterStore.loading = false;
    };

    if (scopeDispose) {
        onScopeDispose(cleanup);
    } else {
        onBeforeUnmount(cleanup);
    }

    return { filterStore, getFilter, filterLoaded, updateFilterTimeout };
}
